import type { FirmaStatusCode } from "../api/types";

// ---------------------------------------------------------------------------
// Shared signature-flow widget (used by DelegaDetail and DelegheVerify)
// ---------------------------------------------------------------------------

function fmt(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("it-IT", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function FirmaStep({ done, active, label }: { done: boolean; active: boolean; label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--font-size-sm)" }}>
      {done
        ? <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-success)" }}>check_circle</span>
        : active
          ? <span className="spinner" style={{ width: 14, height: 14, flexShrink: 0, borderColor: "var(--indaco-brand-primary)", borderTopColor: "transparent" }} />
          : <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-gray-300)" }}>radio_button_unchecked</span>
      }
      <span style={{ color: active ? "var(--indaco-brand-dark)" : done ? "var(--indaco-gray-600)" : "var(--indaco-gray-400)" }}>
        {label}
      </span>
    </div>
  );
}

export interface FirmaBlockProps {
  firmaStatus: FirmaStatusCode;
  deleganteLabel: string;
  delegatoLabel: string;
  waNumber?: string;
  firmaCompletataAt?: string | null;
  firmaLoading: boolean;
  annullaLoading: boolean;
  firmatoLoading: boolean;
  onAvvia: () => void;
  onRetry: () => void;
  onAnnulla: () => void;
  onDownloadFirmato: () => void;
}

export function FirmaBlock({
  firmaStatus,
  deleganteLabel,
  delegatoLabel,
  waNumber,
  firmaCompletataAt,
  firmaLoading,
  annullaLoading,
  firmatoLoading,
  onAvvia,
  onRetry,
  onAnnulla,
  onDownloadFirmato,
}: FirmaBlockProps) {
  const waNum = waNumber || "15551562110";
  const waUrl = (name: string) =>
    `https://wa.me/${waNum}?text=${encodeURIComponent(`Ciao, sto per firmare una delega. Nominativo: ${name}.`)}`;

  if (firmaStatus === "completato") {
    return (
      <div className="card" style={{ background: "var(--indaco-success-light)", border: "2px solid var(--indaco-success)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--font-size-lg)", fontWeight: "var(--font-weight-bold)", color: "var(--indaco-success)", marginBottom: "var(--space-1)" }}>
          <span className="material-symbols-outlined">check_circle</span>
          Processo di firma completato
        </div>
        {firmaCompletataAt && (
          <p style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)", margin: "0 0 var(--space-4)" }}>
            Completato il {fmt(firmaCompletataAt)}
          </p>
        )}
        <button className="btn btn-primary" onClick={onDownloadFirmato} disabled={firmatoLoading}>
          {firmatoLoading
            ? <><span className="spinner" style={{ width: 14, height: 14 }} />Download…</>
            : <><span className="material-symbols-outlined" style={{ fontSize: 18 }}>download</span>Scarica documento firmato</>
          }
        </button>
      </div>
    );
  }

  if (firmaStatus === "attesa_delegante" || firmaStatus === "attesa_delegato") {
    const isDelegante = firmaStatus === "attesa_delegante";
    const activeWaUrl = isDelegante ? waUrl(deleganteLabel) : waUrl(delegatoLabel);
    const activeName  = isDelegante ? deleganteLabel : delegatoLabel;
    return (
      <div className="card" style={{ background: "var(--indaco-gray-50)", border: "1px solid var(--indaco-brand-primary)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginBottom: "var(--space-3)" }}>
          <span className="spinner" style={{ width: 18, height: 18, flexShrink: 0, borderColor: "var(--indaco-brand-primary)", borderTopColor: "transparent" }} />
          <span style={{ fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-brand-dark)" }}>
            Processo di firma in corso — aggiornamento automatico ogni 15s
          </span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", marginBottom: "var(--space-4)" }}>
          <FirmaStep done={!isDelegante} active={isDelegante}
            label={`Delegante — ${deleganteLabel}${isDelegante ? " (in attesa di firma)" : " ✓"}`} />
          <FirmaStep done={false} active={!isDelegante}
            label={`Delegato — ${delegatoLabel}${!isDelegante ? " (in attesa di firma)" : ""}`} />
        </div>
        <a
          href={activeWaUrl} target="_blank" rel="noopener noreferrer"
          style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--font-size-sm)", color: "var(--indaco-brand-dark)", fontWeight: "var(--font-weight-medium)" }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>chat</span>
          Apri chat WhatsApp con {activeName}
        </a>
        <div style={{ marginTop: "var(--space-4)", borderTop: "1px solid var(--indaco-gray-200)", paddingTop: "var(--space-3)" }}>
          <button
            className="btn btn-sm btn-outline"
            style={{ color: "var(--indaco-danger)", borderColor: "var(--indaco-danger)" }}
            onClick={onAnnulla}
            disabled={annullaLoading}
          >
            {annullaLoading
              ? <><span className="spinner" style={{ width: 12, height: 12 }} />Annullamento…</>
              : <><span className="material-symbols-outlined" style={{ fontSize: 16 }}>cancel</span>Annulla firma</>
            }
          </button>
        </div>
      </div>
    );
  }

  if (firmaStatus === "errore") {
    return (
      <div className="card" style={{ background: "var(--indaco-danger-light)", border: "1px solid var(--indaco-danger)" }}>
        <strong style={{ display: "flex", alignItems: "center", gap: "var(--space-1)", color: "var(--indaco-danger)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>cancel</span>
          Errore nel processo di firma
        </strong>
        <p style={{ fontSize: "var(--font-size-sm)", margin: "var(--space-1) 0 var(--space-3)", color: "var(--indaco-gray-700)" }}>
          Si è verificato un errore durante il processo di firma. Riprova o contatta il supporto.
        </p>
        <button className="btn btn-sm btn-outline" onClick={onRetry} disabled={firmaLoading}>
          {firmaLoading ? <span className="spinner" style={{ width: 14, height: 14 }} /> : "Riprova"}
        </button>
      </div>
    );
  }

  // null — non ancora avviato
  return (
    <div>
      <button className="btn btn-primary" onClick={onAvvia} disabled={firmaLoading}>
        {firmaLoading
          ? <><span className="spinner" style={{ width: 14, height: 14 }} />Avvio in corso…</>
          : <><span className="material-symbols-outlined">draw</span>Avvia processo di firma</>
        }
      </button>
    </div>
  );
}
